import clsx from 'clsx';
import React, { memo, useEffect, useMemo, useRef } from 'react';

import useImageAspectRatio from '@hooks/useImageAspectRatio';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { AuthoredContentReactToHtmlParser } from '@utils/reactHtmlParserTransform';

import {
  FALLBACK_IMAGE_CLASS,
  FALLBACK_IMAGE_LOADED_CLASS,
  FLOURISH_EMBED_SELECTOR,
  FLOURISH_SCRIPT_URL,
} from './constants';

interface FlourishWrapperProps {
  fallbackImageURL: string;
  iframe: string;
}

const FlourishWrapper = ({ fallbackImageURL, iframe }: FlourishWrapperProps) => {
  const { aspectRatio, imgRef, onLoad } = useImageAspectRatio();
  const hydrated = useServerSideRendering();
  const fallbackRef = useRef<HTMLDivElement>(null);
  const flourishRef = useRef<HTMLDivElement>(null);

  const aspectRatioStyle = useMemo(() => {
    return aspectRatio ? { aspectRatio } : undefined;
  }, [aspectRatio]);

  const fallbackImage = useMemo(() => {
    return (
      <img
        className="h-auto w-full"
        onLoad={onLoad}
        ref={imgRef}
        src={fallbackImageURL}
        style={aspectRatioStyle}
      />
    );
  }, [fallbackImageURL, aspectRatioStyle, imgRef, onLoad]);

  useEffect(() => {
    // Only load the Flourish embed script if it doesn't already exist on the page
    const scriptTag = document.querySelector(`script[src="${FLOURISH_SCRIPT_URL}"]`);
    global.Flourish = global.Flourish || {};

    if (!scriptTag) {
      global.Flourish.disable_autoload = true;
      const script = document.createElement('script');
      script.src = FLOURISH_SCRIPT_URL;
      document.body.appendChild(script);
    }

    window.setTimeout(() => {
      fallbackRef.current?.classList.add(FALLBACK_IMAGE_LOADED_CLASS);
      if (flourishRef.current) {
        global.Flourish.loadEmbed(flourishRef.current?.querySelector(FLOURISH_EMBED_SELECTOR));
      }
    }, 100);

    return () => {
      scriptTag?.remove();
    };
  }, [iframe]);

  return !hydrated ? (
    <>{fallbackImage}</>
  ) : (
    <figure
      className="relative z-10 m-0 h-auto w-full *:bg-white"
      ref={flourishRef}
      style={aspectRatioStyle}
      aria-live="polite"
    >
      <div ref={fallbackRef} className={clsx(FALLBACK_IMAGE_CLASS, 'absolute -z-10')}>
        {fallbackImage}
      </div>
      {AuthoredContentReactToHtmlParser(iframe)}
      <noscript>{fallbackImage}</noscript>
    </figure>
  );
};

export default memo(FlourishWrapper);
